import React from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMap from 'highcharts/modules/map';
import worldMap from '@highcharts/map-collection/custom/world.geo.json';
import proj4 from 'proj4';
import { colors } from '@alixpartners/ui-components';

import {
  ChartWrapper,
  TooltipWrapper,
  TooltipHeader,
  TooltipRowTitle,
  TooltipRowValue,
  ColorSymbol,
  TooltipRow,
  ColorWrapper,
  TooltipItem,
} from './PersonnelMap.styles';
import {
  MAP_COLORS,
  MARKER_SIZE,
  ZOOM_PADDING,
  MAX_ZOOM_LEVEL,
  ZOOM_FACTOR,
  getExtremesFromPoints,
} from './utils';
import { IHeadcountMapDataItem, ICountryMapDataItem } from './types';
import { IDisplayByType, ICurrencyFormat } from 'utils/types';
import { renderToString } from 'react-dom/server';
import {
  DECIMALS_FORMATS,
  CURRENCIES_SIGNS,
  UNITS_DIVIDERS,
  UNITS_SIGNS,
} from 'utils/constants';
import numeral from 'numeral';

if (typeof window !== 'undefined') {
  window.proj4 = window.proj4 || proj4;
}

HighchartsMap(Highcharts);

const addMapPieSeries = (
  chart: any,
  data: IHeadcountMapDataItem & { country: string | null },
  isSpend: boolean,
  minValue: number,
  maxValue: number,
  showInLegend: boolean,
  isInactive: boolean
) => {
  chart.addSeries(
    {
      type: 'mappie',
      style: { fontFamily: 'Roboto' },
      name: data.name,
      zIndex: 6,
      opacity: 0.9,
      animation: false,
      showInLegend,
      country: data.country,
      data: [
        {
          name: 'Contractor',
          y: isSpend ? data.contractorCost : data.contractor,
          color: isInactive ? MAP_COLORS[6] : MAP_COLORS[5],
        },
        {
          name: 'Permanent',
          y: isSpend ? data.permanentCost : data.permanent,
          color: isInactive ? MAP_COLORS[3] : MAP_COLORS[4],
        },
      ],
      center: {
        lat: data.lat,
        lon: data.lon,
      },
      sizeFormatter: function () {
        const yAxis = this.chart.yAxis[0];
        const zoomFactor =
          (yAxis.dataMax - yAxis.dataMin) / (yAxis.max - yAxis.min);
        const max = Math.max(
          MARKER_SIZE.max *
            (Math.min(zoomFactor, MAX_ZOOM_LEVEL) / ZOOM_FACTOR),
          MARKER_SIZE.max
        );
        const min = Math.max(
          MARKER_SIZE.min *
            (Math.min(zoomFactor, MAX_ZOOM_LEVEL) / ZOOM_FACTOR),
          MARKER_SIZE.min
        );
        const sizeDelta = max - min;
        const valueDelta = maxValue - minValue;
        const totalValue = isSpend ? data.cost : data.headcount;
        const size = min + ((totalValue - minValue) * sizeDelta) / valueDelta;

        return size;
      },
    },
    false
  );
};

interface IProps {
  countryData: ICountryMapDataItem[];
  headcountData: IHeadcountMapDataItem[];
  onCountrySelect(country?: string, callback?: () => void): void;
  displayType: IDisplayByType;
  permanentContractorChecked: boolean;
  selectedCountry?: string;
  format: ICurrencyFormat;
}

export class PersonnelMap extends React.PureComponent<IProps> {
  chartRef = React.createRef<any>();

  componentDidUpdate(prevProps: IProps) {
    const {
      headcountData,
      permanentContractorChecked,
      selectedCountry,
      displayType,
    } = this.props;

    if (
      permanentContractorChecked &&
      (headcountData !== prevProps.headcountData ||
        permanentContractorChecked !== prevProps.permanentContractorChecked ||
        selectedCountry !== prevProps.selectedCountry ||
        displayType !== prevProps.displayType)
    ) {
      this.drawHeadcountPies();
    }
  }

  getMapSeriesData = () => {
    const { countryData, selectedCountry } = this.props;
    const config = {
      borderWidth: 0.5,
      borderColor: colors.grey8,
    };

    if (selectedCountry) {
      return countryData.map((item) => ({
        ...item,
        ...config,
        id: item.name,
        color: item.name === selectedCountry ? MAP_COLORS[1] : MAP_COLORS[2],
      }));
    }

    return countryData.map((item) => ({ ...item, ...config, id: item.name }));
  };

  drawHeadcountPies = () => {
    const {
      permanentContractorChecked,
      selectedCountry,
      countryData,
      displayType,
    } = this.props;

    if (!permanentContractorChecked) {
      return;
    }

    const chart = this.chartRef?.current?.chart;
    const isSpend = displayType === IDisplayByType.COST;
    const locationsData = this.getLocationsData();
    const values = locationsData.map((item) =>
      isSpend ? item.cost : item.headcount
    );
    const maxValue = Math.max(...values);
    const minValue = Math.min(...values);
    const selectedCode = countryData.find(
      (item) => item.name === selectedCountry
    )?.code;

    if (chart) {
      let isLegendShown = false;

      locationsData.forEach((location, index) => {
        const isInactive =
          !!selectedCode && selectedCode !== location.countryCode;
        const showInLegend = !isLegendShown
          ? selectedCode
            ? !isInactive
            : index === 0
          : false;

        if (!isLegendShown && showInLegend) {
          isLegendShown = true;
        }

        const countryName = this.getCountryName(location.countryCode);
        const data = {
          ...location,
          country: countryName,
          name: `${location.name}${
            location.countryCode ? `, ${countryName}` : ''
          }`,
        };

        addMapPieSeries(
          chart,
          data,
          isSpend,
          minValue,
          maxValue,
          showInLegend,
          isInactive
        );
      });

      chart.redraw();
    }
  };

  pointFormat = (value: number) => {
    const { displayType, format } = this.props;
    const decimalFormat = DECIMALS_FORMATS[format.decimal];
    const isSpend = displayType === IDisplayByType.COST;
    const unitValue = isSpend ? value / UNITS_DIVIDERS[format.unit] : value;
    const formattedValue = numeral(unitValue).format(`0,0${decimalFormat}`);

    if (isSpend) {
      return `${CURRENCIES_SIGNS[format.currency]}${formattedValue}${
        UNITS_SIGNS[format.unit]
      }`;
    }

    return formattedValue;
  };

  getLocationsData = () => {
    const { headcountData } = this.props;

    return headcountData.filter((item) => item.lat && item.lon);
  };

  getCountryName = (code: string | null) => {
    if (!code) {
      return null;
    }

    const { countryData } = this.props;
    const obj = countryData.find((item) => item.code === code);

    return obj ? obj.name : null;
  };

  updateBubbleSize = () => {
    if (!this.chartRef || !this.chartRef.current) {
      return;
    }

    const { chart } = this.chartRef.current;
    const bubbleSeries = chart.get('bubbles');

    if (!bubbleSeries) {
      return;
    }

    const yAxis = chart.yAxis[0];
    const zoomFactor =
      (yAxis.dataMax - yAxis.dataMin) / (yAxis.max - yAxis.min);
    const max = Math.max(
      MARKER_SIZE.max * (Math.min(zoomFactor, MAX_ZOOM_LEVEL) / ZOOM_FACTOR),
      MARKER_SIZE.max
    );
    const min = Math.max(
      MARKER_SIZE.min * (Math.min(zoomFactor, MAX_ZOOM_LEVEL) / ZOOM_FACTOR),
      MARKER_SIZE.min
    );

    bubbleSeries.options.maxSize = max;
    bubbleSeries.options.minSize = min;
  };

  unselectCountry = () => {
    const { onCountrySelect } = this.props;
    const { chart } = this.chartRef?.current;

    onCountrySelect(undefined);

    if (chart) {
      chart.yAxis[0].setExtremes(null, null);
      chart.xAxis[0].setExtremes(null, null);
    }
  };

  getOptions = () => {
    const {
      onCountrySelect,
      permanentContractorChecked,
      displayType,
      selectedCountry,
      countryData,
    } = this.props;
    const tooltipFormat = this.pointFormat;
    let zoomFactor = null;

    if (this.chartRef && this.chartRef.current) {
      const { chart } = this.chartRef.current;
      const yAxis = chart.yAxis[0];

      zoomFactor = (yAxis.dataMax - yAxis.dataMin) / (yAxis.max - yAxis.min);
    }

    return {
      chart: {
        map: worldMap,
        borderWidth: 0,
        style: { fontFamily: 'Roboto' },
        events: {
          click: this.unselectCountry,
        },
        animation: false,
      },
      title: {
        text: '',
      },
      exporting: { enabled: false },
      credits: {
        enabled: false,
      },
      yAxis: {
        events: {
          afterSetExtremes: this.updateBubbleSize,
        },
      },
      legend: {
        enabled: true,
        itemStyle: {
          fontSize: '11px',
          fontWeight: 'normal',
          lineHeight: '10px',
          color: colors.grey8,
        },
        symbolRadius: 1,
        symbolHeight: 16,
        symbolWidth: 16,
        lineHeight: 16,
        symbolPadding: 8,
        useHTML: true,
        padding: 15,
      },
      mapNavigation: {
        enabled: true,
      },
      plotOptions: {
        mappie: {
          point: {
            events: {
              legendItemClick: () => false,
            },
          },
        },
      },
      tooltip: {
        useHTML: true,
        backgroundColor: colors.white,
        borderWidth: 0,
        borderRadius: 2,
        formatter: function () {
          // @ts-ignore
          const { point, series } = this as any;
          // @ts-ignore
          if (!series || !series.name || series.name === 'map') {
            return false;
          }

          if (permanentContractorChecked) {
            return renderToString(
              <TooltipWrapper>
                <TooltipHeader>{series.name}</TooltipHeader>

                <TooltipRow>
                  <div />
                  <TooltipRowTitle>Total</TooltipRowTitle>
                  <TooltipRowValue>
                    {tooltipFormat(point.total)}
                  </TooltipRowValue>
                </TooltipRow>
                {series.data.map((pointData: any) => (
                  <TooltipRow key={pointData.name}>
                    <ColorWrapper>
                      <ColorSymbol color={pointData.color} />
                    </ColorWrapper>
                    <TooltipRowTitle>{pointData.name}</TooltipRowTitle>
                    <TooltipRowValue>
                      {tooltipFormat(pointData.y)} (
                      {Math.round((pointData.y / point.total) * 100)}%)
                    </TooltipRowValue>
                  </TooltipRow>
                ))}
              </TooltipWrapper>
            );
          }

          return renderToString(
            <TooltipWrapper>
              <TooltipHeader>
                {point.name}
                {point.country && `, ${point.country}`}
              </TooltipHeader>
              <TooltipItem>
                <TooltipRowTitle>
                  Total {point.series.name.toLowerCase()}
                </TooltipRowTitle>
                <TooltipRowValue>{tooltipFormat(point.z)}</TooltipRowValue>
              </TooltipItem>
            </TooltipWrapper>
          );
        },
      },
      series: [
        {
          data: this.getMapSeriesData(),
          showInLegend: false,
          joinBy: ['iso-a3', 'code'],
          nullColor: colors.grey2,
          color: MAP_COLORS[1],
          name: 'map',
          animation: false,
          borderColor: colors.grey5,
          borderWidth: 0.25,
          cursor: 'pointer',
          states: {
            hover: {
              brightness: 0.05,
              borderWidth: 0.25,
              borderColor: colors.grey8,
            },
          },
          events: {
            click: (e: any) => {
              const point = e.point;

              onCountrySelect(point.id, () => {
                const { selectedCountry, permanentContractorChecked } =
                  this.props;

                if (this.chartRef && this.chartRef.current) {
                  const { chart } = this.chartRef.current;

                  if (selectedCountry) {
                    let boundingBox;

                    if (permanentContractorChecked) {
                      const pieSeriesPoints = chart.series
                        .filter(
                          (item: any) =>
                            item.type === 'mappie' &&
                            item.options.country === selectedCountry
                        )
                        .map((item: any) => {
                          return chart.fromLatLonToPoint(
                            item.userOptions.center
                          );
                        });

                      if (pieSeriesPoints) {
                        boundingBox = getExtremesFromPoints(pieSeriesPoints);
                      }
                    } else {
                      const bubbleSeries = chart.get('bubbles');

                      if (bubbleSeries) {
                        const countryPoints = bubbleSeries.points.filter(
                          (item: any) => item.country === selectedCountry
                        );

                        if (countryPoints && countryPoints.length > 0) {
                          boundingBox = getExtremesFromPoints(countryPoints);
                        }
                      }
                    }

                    if (boundingBox) {
                      // Center the map to bounding box center
                      chart.mapZoom(
                        1,
                        (boundingBox.maxX + boundingBox.minX) / 2,
                        (boundingBox.maxY + boundingBox.minY) / 2,
                        (boundingBox.maxX + boundingBox.minX) / 2,
                        (boundingBox.maxY + boundingBox.minY) / 2
                      );

                      // Set extremes of bounding box
                      chart.yAxis[0].setExtremes(
                        boundingBox.minY - ZOOM_PADDING,
                        boundingBox.maxY + ZOOM_PADDING
                      );
                      chart.xAxis[0].setExtremes(
                        boundingBox.minX - ZOOM_PADDING,
                        boundingBox.maxX + ZOOM_PADDING
                      );

                      return;
                    }

                    const country = chart.get(selectedCountry);

                    if (country) {
                      // Fallback if no points on series
                      country.zoomTo();
                      chart.mapZoom(1.5);
                    }
                  } else {
                    chart.yAxis[0].setExtremes(null, null);
                    chart.xAxis[0].setExtremes(null, null);
                  }
                }
              });
            },
          },
        },
        ...(!permanentContractorChecked
          ? [
              {
                id: 'bubbles',
                type: 'mapbubble',
                animation: false,
                name:
                  displayType === IDisplayByType.COST ? 'Spend' : 'Headcount',
                minSize: zoomFactor
                  ? Math.max(
                      MARKER_SIZE.min *
                        (Math.min(zoomFactor, MAX_ZOOM_LEVEL) / ZOOM_FACTOR),
                      MARKER_SIZE.min
                    )
                  : MARKER_SIZE.min,
                maxSize: zoomFactor
                  ? Math.max(
                      MARKER_SIZE.max *
                        (Math.min(zoomFactor, MAX_ZOOM_LEVEL) / ZOOM_FACTOR),
                      MARKER_SIZE.max
                    )
                  : MARKER_SIZE.max,
                opacity: 0.8,
                showInLegend: false,
                marker: {
                  lineWidth: 1,
                  lineColor: colors.white,
                  fillOpacity: 0.8,
                  states: { hover: { lineWidth: 1 } },
                },
                states: {
                  hover: { opacity: 1, marker: { opacity: 1 } },
                },
                data: this.getLocationsData().map((item) => {
                  const { name, lat, lon, headcount, cost, countryCode } = item;
                  const selectedCode = countryData.find(
                    (item) => item.name === selectedCountry
                  )?.code;
                  const inactive =
                    selectedCountry &&
                    selectedCode &&
                    selectedCode !== countryCode;

                  return {
                    name,
                    lat,
                    lon,
                    z: displayType === IDisplayByType.COST ? cost : headcount,
                    color: inactive ? MAP_COLORS[6] : MAP_COLORS[0],
                    country: this.getCountryName(countryCode),
                  };
                }),
              },
            ]
          : []),
      ],
    };
  };

  render() {
    return (
      <ChartWrapper>
        <HighchartsReact
          highcharts={Highcharts}
          options={this.getOptions()}
          constructorType="mapChart"
          ref={this.chartRef}
          updateArgs={[true, true, false]}
        />
      </ChartWrapper>
    );
  }
}

// Custom mappie series type
Highcharts.seriesType(
  'mappie',
  'pie',
  {
    center: null, // Can't be array by default anymore
    clip: true, // For map navigation
    states: {
      hover: {
        enabled: false,
      },
      inactive: {
        enabled: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
  },
  {
    getCenter: function () {
      const options = this.options;
      const chart = this.chart;
      const slicingRoom = 2 * (options.slicedOffset || 0);

      if (!options.center) {
        options.center = [null, null]; // Do the default here instead
      }

      // Handle lat/lon support
      if (options.center.lat !== undefined) {
        const point = chart.fromLatLonToPoint(options.center);

        options.center = [
          chart.xAxis[0].toPixels(point.x, true),
          chart.yAxis[0].toPixels(point.y, true),
        ];
      }

      // Replace lat/lon with plotX/plotY
      if (options.center.plotX !== undefined) {
        options.center = [options.center.plotX, options.center.plotY];
      }

      // Handle dynamic size
      if (options.sizeFormatter) {
        options.size = options.sizeFormatter.call(this);
      }
      // Call parent function
      // @ts-ignore
      const result = Highcharts.seriesTypes.pie.prototype.getCenter.call(this);
      // Must correct for slicing room to get exact pixel pos
      result[0] -= slicingRoom;
      result[1] -= slicingRoom;
      return result;
    },
    translate: function (p: any) {
      this.options.center = this.userOptions.center;
      this.center = this.getCenter();
      // @ts-ignore
      return Highcharts.seriesTypes.pie.prototype.translate.call(this, p);
    },
  }
);
